package com.arenaproplay.admin.domain.admin

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// DTOs for the Content Studio seeding endpoints (/v1/admin/seed/*).

@Serializable
data class SeedUsersRequest(
    @SerialName("count") val count: Int,
    @SerialName("city") val city: String? = null,
    @SerialName("sports") val sports: List<String> = emptyList(),
)

/** One hand-filled fake user; every null/empty field is generated by the backend. */
@Serializable
data class SeedCustomUserRequest(
    @SerialName("fullName") val fullName: String? = null,
    @SerialName("username") val username: String? = null,
    @SerialName("email") val email: String? = null,
    @SerialName("gender") val gender: String? = null,
    @SerialName("bio") val bio: String? = null,
    @SerialName("city") val city: String? = null,
    /** Decimal strings from the address-autocomplete pick. */
    @SerialName("cityLat") val cityLat: String? = null,
    @SerialName("cityLng") val cityLng: String? = null,
    @SerialName("country") val country: String? = null,
    /** YYYY-MM-DD */
    @SerialName("dateOfBirth") val dateOfBirth: String? = null,
    @SerialName("languages") val languages: List<String> = emptyList(),
    @SerialName("interestSports") val interestSports: List<String> = emptyList(),
    @SerialName("interestActivities") val interestActivities: List<String> = emptyList(),
    @SerialName("interestLifestyle") val interestLifestyle: List<String> = emptyList(),
)

@Serializable
data class SeedUserRow(
    @SerialName("id") val id: String,
    @SerialName("fullName") val fullName: String,
    @SerialName("username") val username: String,
    @SerialName("email") val email: String,
    @SerialName("gender") val gender: String? = null,
    @SerialName("bio") val bio: String? = null,
    @SerialName("city") val city: String? = null,
    @SerialName("cityLat") val cityLat: String? = null,
    @SerialName("cityLng") val cityLng: String? = null,
    @SerialName("country") val country: String? = null,
    /** YYYY-MM-DD */
    @SerialName("dateOfBirth") val dateOfBirth: String? = null,
    @SerialName("avatarUrl") val avatarUrl: String? = null,
    @SerialName("languages") val languages: List<String> = emptyList(),
    @SerialName("interestSports") val interestSports: List<String> = emptyList(),
    @SerialName("interestActivities") val interestActivities: List<String> = emptyList(),
    @SerialName("interestLifestyle") val interestLifestyle: List<String> = emptyList(),
    @SerialName("createdAt") val createdAt: String,
)

@Serializable
data class SeedCardTemplate(
    @SerialName("id") val id: String,
    @SerialName("name") val name: String,
    @SerialName("imageUrl") val imageUrl: String,
    @SerialName("isDefault") val isDefault: Boolean,
)

@Serializable
data class SeedAvatarResult(
    @SerialName("userId") val userId: String,
    @SerialName("profileCardId") val profileCardId: String,
    @SerialName("avatarUrl") val avatarUrl: String,
    @SerialName("cardTemplateName") val cardTemplateName: String,
)

/** One hand-written post — mirrors the app's create-post form. */
@Serializable
data class SeedCustomPostRequest(
    @SerialName("authorId") val authorId: String,
    @SerialName("content") val content: String = "",
    @SerialName("imageUrls") val imageUrls: List<String> = emptyList(),
    /** 1–6, same layout codes as the app. */
    @SerialName("layout") val layout: Int = 1,
)

@Serializable
data class SeedPostsRequest(
    @SerialName("authorIds") val authorIds: List<String>,
    @SerialName("countPerAuthor") val countPerAuthor: Int = 1,
    @SerialName("imageUrls") val imageUrls: List<String> = emptyList(),
)

@Serializable
data class SeedEventRequest(
    @SerialName("creatorId") val creatorId: String? = null,
    @SerialName("sportId") val sportId: String? = null,
    @SerialName("title") val title: String? = null,
    @SerialName("description") val description: String? = null,
    @SerialName("dateTime") val dateTime: String? = null,
    @SerialName("endDateTime") val endDateTime: String? = null,
    @SerialName("city") val city: String? = null,
    @SerialName("maxPlayers") val maxPlayers: Int? = null,
    @SerialName("isPro") val isPro: Boolean = false,
    @SerialName("participantsCount") val participantsCount: Int = 0,
    @SerialName("privacy") val privacy: String = "PUBLIC",
    @SerialName("format") val format: Int? = null,
    /** Decimal string, e.g. "150.00". */
    @SerialName("cost") val cost: String? = null,
    @SerialName("address") val address: String? = null,
    @SerialName("latitude") val latitude: String? = null,
    @SerialName("longitude") val longitude: String? = null,
    @SerialName("imageUrl") val imageUrl: String? = null,
)

@Serializable
data class SeedEventResult(
    @SerialName("id") val id: String,
    @SerialName("title") val title: String,
    @SerialName("participantsAdded") val participantsAdded: Int,
)

@Serializable
data class SeedParticipantsRequest(
    @SerialName("userIds") val userIds: List<String> = emptyList(),
    @SerialName("randomCount") val randomCount: Int = 0,
)

@Serializable
data class SeedEventStatusRequest(
    @SerialName("status") val status: String,
)

@Serializable
data class SeedEventStatusResult(
    @SerialName("id") val id: String,
    @SerialName("status") val status: String,
)

@Serializable
data class SeedPostResult(
    @SerialName("id") val id: String,
)

/** One story (24h, single image) on behalf of a fake user. */
@Serializable
data class SeedCustomStoryRequest(
    @SerialName("authorId") val authorId: String,
    @SerialName("imageUrl") val imageUrl: String,
)

@Serializable
data class SeedParticipant(
    @SerialName("userId") val userId: String,
    @SerialName("name") val name: String,
    @SerialName("username") val username: String,
    @SerialName("avatarUrl") val avatarUrl: String? = null,
    @SerialName("status") val status: String,
    @SerialName("isAdmin") val isAdmin: Boolean = false,
)

@Serializable
data class SeedCount(
    @SerialName("created") val created: Int,
)

@Serializable
data class SeedPurgeResult(
    @SerialName("usersDeleted") val usersDeleted: Int,
    @SerialName("eventsDeleted") val eventsDeleted: Int,
)
